/**
 * Terminology Icon Map
 *
 * Thin wrappers over the canonical vocabulary in `@/lib/icon-vocabulary`
 * (#14985), for rendering icons from terminology configuration.
 *
 * `TERMINOLOGY_ICONS` lives here rather than in `@/lib/terminology-types`
 * because it is derived from `ICON_ENTRIES`, which carries glyph components —
 * and `terminology-types` is imported by the MCP server bundle, which must
 * not pull in the icon set.
 */

import { Circle, type LucideIcon } from '@/components/icons'
import { ICON_ENTRIES, resolveIcon } from '@/lib/icon-vocabulary'

/**
 * Canonical icon name → component. Derived from `ICON_ENTRIES`; not a list
 * of its own.
 */
export const TERMINOLOGY_ICON_MAP: Readonly<Record<string, LucideIcon>> =
  Object.fromEntries(ICON_ENTRIES.map(entry => [entry.name, entry.icon]))

/**
 * The icons a terminology term can pick — the canonical vocabulary's names
 * and labels.
 */
export const TERMINOLOGY_ICONS: ReadonlyArray<{ name: string; label: string }> =
  ICON_ENTRIES.map(({ name, label }) => ({ name, label }))

/**
 * Default fallback icon when an unknown icon name is used
 */
export const DEFAULT_TERM_ICON: LucideIcon = Circle

/**
 * Get a Lucide icon component by name
 *
 * @param iconName - The icon name from terminology config
 * @returns The Lucide icon component, or Circle as fallback
 */
export function getTermIcon(iconName: string): LucideIcon {
  return resolveIcon(iconName) ?? DEFAULT_TERM_ICON
}

/**
 * Check if an icon name is valid (resolves to a glyph)
 */
export function isValidTermIcon(iconName: string): boolean {
  return resolveIcon(iconName) !== null
}
